const AUTH_ROUTES = {
  login: '/login',
  newPassword: '/new-password',
  register: '/register',
  twoFactor: '/two-factor',
  twoFactorSetup: '/two-factor/setup',
  dashboard: '/',
} as const;

const AUTH_STAGE_ROUTES = {
  'anonymous': AUTH_ROUTES.login,
  'forbidden': AUTH_ROUTES.login,
  'mfa_enroll': AUTH_ROUTES.twoFactorSetup,
  'mfa_verify': AUTH_ROUTES.twoFactor,
  'ready': AUTH_ROUTES.dashboard,
} as const;

const TOTP_CODE_LENGTH = 6;

const PASSWORD_MIN_LENGTH = 12;

const PASSWORD_MAX_BYTES = 72;

const PASSWORD_RULE_COPY = 'Twelve characters or more, with an upper-case letter, a lower-case letter, a digit and a symbol.';

const LOGIN_COPY = {
  title: 'Log in',
  emailLabel: 'Email',
  emailPlaceholder: 'you@example.com',
  passwordLabel: 'Password',
  forgotPassword: 'Forgot password?',
  submit: 'Log in',
} as const;

const LOGIN_NOTE_LINES = [
  'Two-step verification is required for every staff account.',
] as const;

const RESET_PASSWORD_COPY = {
  title: 'Reset password',
  description: 'Enter the email on your staff account and we’ll send a secure reset link.',
  emailLabel: 'Email',
  emailPlaceholder: 'you@example.com',
  note: 'Resetting your password does not reset two-step verification.',
  cancel: 'Cancel',
  submit: 'Send reset link',
  done: 'Done',
  sentDescription: 'If that address belongs to a staff account, a reset link is on its way. '
    + 'The link lasts ten minutes and can be used once.',
} as const;

const NEW_PASSWORD_COPY = {
  title: 'Set a new password',
  description: PASSWORD_RULE_COPY,
  passwordLabel: 'New password',
  confirmationLabel: 'Repeat new password',
  codeLabel: 'Code from your authenticator app',
  submit: 'Save password',
  backToLogin: 'Back to log in',
  expiredTitle: 'This link no longer works',
  expiredDescription: 'A reset link lasts ten minutes and can be used once. Request a new one.',
  requestAgain: 'Request a new link',
} as const;

const REGISTER_COPY = {
  title: 'Welcome to The 500',
  description: 'Set up your staff account. Two-step verification comes right after.',
  joiningAs: 'Joining as',
  emailLabel: 'Email',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  passwordLabel: 'Password',
  passwordHint: PASSWORD_RULE_COPY,
  confirmationLabel: 'Repeat password',
  submit: 'Create account',
  backToLogin: 'Back to log in',
  unusableTitle: 'This invitation cannot be used',
} as const;

const REGISTER_NOTE_LINES = [
  'Two-step verification is required for every staff account.',
] as const;

const REGISTRATION_REASON_COPY: Record<string, string> = {
  'unknown': 'This link is not valid. Open the whole link from the invitation email, or ask your Super Admin for a new one.',
  'missing': 'This page opens from the link in your invitation email.',
  'expired': 'This invitation has expired — invitations work for 24 hours. Ask your Super Admin for a new link.',
  'consumed': 'This invitation has already been used. Log in with your email and password.',
  'revoked': 'This invitation was withdrawn. Ask your Super Admin if you still need access.',
  'account_exists': 'An account already exists for this address. Log in with your email and password.',
  'rate_limited': 'Too many attempts from this device. Try again in fifteen minutes.',
  'invalid_payload': 'Some details were not accepted. Check the fields and try again.',
};

const NEW_PASSWORD_NOTE_LINES = [
  'Saving a new password signs the account out of every device.',
] as const;

const RESET_PASSWORD_FORM_ID = 'reset-password-form';

const RESET_PASSWORD_NOTE_LINES = [
  'Two-step verification is still required after a password reset.',
] as const;

const TOTP_ENROLL_COPY = {
  title: 'Two-step verification',
  description: 'Scan the code with your authenticator app, then enter its first 6-digit code.',
  manualKeyLabel: 'Can’t scan? Enter this setup key manually',
  submit: 'Verify & continue',
  failedTitle: 'Enrollment could not start',
  backToLogin: 'Back to log in',
} as const;

const TOTP_ENROLL_NOTE_LINES = [
  'Enrollment is required on your first sign-in — there is no opt-out and no SMS fallback.',
  'Lost device? A Super Admin re-issues enrollment.',
] as const;

const TOTP_CHALLENGE_COPY = {
  title: 'Enter your verification code',
  description: 'Open your authenticator app — codes rotate every 30 seconds.',
  submit: 'Verify',
  missingTitle: 'No authenticator on this account',
  missingDescription: 'Enrollment has not been completed yet. Start it again from the beginning.',
  startEnrollment: 'Set up two-step verification',
} as const;

const TOTP_CHALLENGE_NOTE_LINES = [
  'A few incorrect attempts place a short hold on the account.',
  'Lost your device? Recovery goes through a Super Admin.',
] as const;

const SWITCH_ACCOUNT_COPY = { label: 'Sign in with a different account' } as const;

const SESSION_UNAVAILABLE_COPY = {
  title: 'Can’t reach the server',
  description: 'Your session could not be confirmed. Check the connection and try again.',
  retry: 'Try again',
} as const;

const AUTH_ERROR_COPY: Record<string, string> = {
  'invalid_credentials': 'Email or password is incorrect.',
  'email_not_confirmed': 'This account is not active yet. A Super Admin can finish setting it up.',
  'user_banned': 'This account is on hold. A Super Admin can restore it.',
  'over_request_rate_limit': 'Too many attempts. Try again in a few minutes.',
  'over_email_send_rate_limit': 'Too many emails just went out. Try again in a few minutes.',
  'same_password': 'That is the password you already have. Pick a different one.',
  'weak_password': 'Too weak: twelve characters or more, with upper case, lower case, a digit and a symbol.',
  'session_expired': 'That reset link has expired. Request a new one.',
  'otp_expired': 'This reset link has expired or was already used. Request a new one.',
  'mfa_code_required': 'Enter the six-digit code from your authenticator app.',
  'insufficient_aal': 'Enter the six-digit code from your authenticator app.',
  'mfa_verification_failed': 'That code is not right. Codes rotate every 30 seconds.',
  'mfa_verification_rejected': 'That code was rejected. Try the next one your app shows.',
  'mfa_challenge_expired': 'That code took too long. Enter the current one.',
  'mfa_factor_not_found': 'No authenticator is enrolled on this account.',
  'mfa_factor_name_conflict': 'An unfinished enrollment is already on this account. Reload to start over.',
  'too_many_enrolled_mfa_factors': 'This account already has the maximum number of authenticators.',
  '429': 'Too many attempts. Try again in a few minutes.',
  'NETWORK': 'No connection. Check your network and try again.',
};

const AUTH_ERROR_FALLBACK = 'Something went wrong. Try again.';

export {
  AUTH_ERROR_COPY,
  AUTH_ERROR_FALLBACK,
  AUTH_ROUTES,
  AUTH_STAGE_ROUTES,
  LOGIN_COPY,
  LOGIN_NOTE_LINES,
  NEW_PASSWORD_COPY,
  NEW_PASSWORD_NOTE_LINES,
  PASSWORD_MAX_BYTES,
  PASSWORD_MIN_LENGTH,
  REGISTER_COPY,
  REGISTER_NOTE_LINES,
  REGISTRATION_REASON_COPY,
  RESET_PASSWORD_COPY,
  RESET_PASSWORD_FORM_ID,
  RESET_PASSWORD_NOTE_LINES,
  SESSION_UNAVAILABLE_COPY,
  SWITCH_ACCOUNT_COPY,
  TOTP_CHALLENGE_COPY,
  TOTP_CHALLENGE_NOTE_LINES,
  TOTP_CODE_LENGTH,
  TOTP_ENROLL_COPY,
  TOTP_ENROLL_NOTE_LINES,
};
